import React from 'react';
import {
  deriveBillingState,
  billingStateLabel,
  billingStateHelp,
  billingStatePillClass,
  summarizeBillingStates,
  SILENT_WARNING_DAYS,
  type BillingAgentLike,
  type BillingState,
} from './billingState';

interface BillingPillProps {
  agent: BillingAgentLike;
  /** Offered on silent hosts only: archiving stops billing that day. */
  onArchive?: () => void;
  /** The fleet list arms a two-click confirm; when armed, the link says so. */
  confirming?: boolean;
  now?: Date;
}

/**
 * One host's billing state for the fleet table. Amber is reserved for the
 * silent-but-billed case, the only state where the customer has a decision to
 * make; every other state is quiet. The word always accompanies the colour.
 */
export const BillingPill: React.FC<BillingPillProps> = ({ agent, onArchive, confirming = false, now }) => {
  const { state, silentDays } = deriveBillingState(agent, now);
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${billingStatePillClass(state)}`}
        title={billingStateHelp(state)}
        data-billing-state={state}
      >
        {billingStateLabel(state, silentDays)}
      </span>
      {state === 'silent_warning' && onArchive && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onArchive(); }}
          className={`text-xs font-medium underline decoration-amber-400 ${confirming ? 'text-red-700 hover:text-red-800' : 'text-amber-800 hover:text-amber-900'}`}
        >
          {confirming ? 'Confirm archive' : 'Archive'}
        </button>
      )}
    </span>
  );
};

interface SilentHostsBannerProps {
  agents: BillingAgentLike[];
  active?: BillingState | null;
  onFilter: (state: BillingState | null) => void;
  now?: Date;
}

const chipOrder: Array<{ state: BillingState; label: string }> = [
  { state: 'billable', label: 'Billable' },
  { state: 'silent_warning', label: 'Silent' },
  { state: 'stale', label: 'Stale' },
  { state: 'never_seen', label: 'Never seen' },
  { state: 'archived', label: 'Archived' },
];

/**
 * Billing-state chips with counts, and a one-line banner whenever any host is
 * silent for seven days or more and therefore still billed (decision 68).
 */
export const SilentHostsBanner: React.FC<SilentHostsBannerProps> = ({ agents, active, onFilter, now }) => {
  const counts = summarizeBillingStates(agents, now);
  const silent = counts.silent_warning;
  return (
    <div className="space-y-3" data-testid="billing-summary">
      {silent > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>
            <span className="font-medium">{silent} {silent === 1 ? 'host has' : 'hosts have'} been silent for {SILENT_WARNING_DAYS} or more days</span>
            {' '}and {silent === 1 ? 'is' : 'are'} still billed. Archive the ones you have retired; billing stops the day you do.
          </span>
          <button
            type="button"
            onClick={() => onFilter(active === 'silent_warning' ? null : 'silent_warning')}
            className="rounded-md bg-amber-200 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-300"
          >
            {active === 'silent_warning' ? 'Show all' : 'Show silent hosts'}
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by billing state">
        <span className="text-xs uppercase tracking-wider text-gray-500 mr-1">Billing</span>
        {chipOrder.map(({ state, label }) => {
          const n = counts[state];
          const isActive = active === state;
          return (
            <button
              key={state}
              type="button"
              onClick={() => onFilter(isActive ? null : state)}
              aria-pressed={isActive}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                isActive
                  ? 'border-gray-800 bg-gray-800 text-white'
                  : n === 0
                    ? 'border-gray-200 bg-white text-gray-400'
                    : state === 'silent_warning'
                      ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
              <span className="font-mono tabular-nums">{n}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BillingPill;
